// rm-list.js — hierarchical list browser.
//
// Mirrors `Assets/_App/UI/Screens/ListScreen.cs`. Renders any RMListNode:
// drill into a child list pushes a new <rm-list> instance via the router;
// drill into a child item pushes a <rm-player>. Back-navigation is via the
// History API.
//
// Brief 018 adds list-level gating: locked rows (items the user can't play /
// lists with no free descendant) render greyed with a lock glyph and pop the
// `<rm-upgrade-card>` upsell on click/Enter/Space instead of navigating. The
// gate decision is read from the DOM-free `entitlement-service` (booleans
// only); this screen does the rendering (CLAUDE.md Rule #1).
//
// A future brief will add completion badges (`<rm-progress-bubble>`),
// per-list-context required-plays display, and a tablet/desktop split-pane
// layout. The Unity reference for that work is
// `Assets/_App/UI/Components/ListItemCell.cs`.

import { RMComponent } from "../core/component.js";
import { html } from "../core/html.js";
import * as content from "../engines/content-loader.js";
import * as entitlement from "../engines/entitlement-service.js";
import { LOCK } from "../ui/icons.js";

export class RMList extends RMComponent {
  static props = ["listId"];
  /** @type {string | null} */
  listId = null;

  /** Teardown for the currently-mounted upsell overlay, if any. */
  /** @type {(() => void) | null} */
  _upsellCleanup = null;
  /** Locked row that opened the current overlay; focus returns here on close. */
  /** @type {HTMLElement | null} */
  _upsellTrigger = null;

  static styles = `
    :host { display: block; padding: 1rem; }
    h2 { margin: 0 0 1rem; font-size: 1.25rem; }
    ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.25rem; }
    .row {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.75rem; border-radius: 0.5rem;
      text-decoration: none; color: var(--rm-fg); background: var(--rm-surface);
    }
    a.row:hover { background: var(--rm-surface-hover); }
    .row .text { flex: 1 1 auto; min-width: 0; }
    .meta { color: var(--rm-muted); font-size: 0.8rem; }
    .row.locked { opacity: 0.55; cursor: pointer; }
    .row.locked .text { color: var(--rm-muted); }
    .row.locked .lock {
      flex: none; display: inline-flex; align-items: center;
      color: var(--rm-muted); font-size: 1.1rem;
    }
    .row.locked .lock svg { display: block; }
    .row.locked:focus-visible { outline: 2px solid var(--rm-accent); outline-offset: 2px; }

    .upsell-overlay {
      position: fixed; inset: 0; z-index: 50;
      display: flex; align-items: center; justify-content: center;
      padding: 1rem;
      background: color-mix(in srgb, var(--rm-bg) 72%, transparent);
    }
    .upsell-overlay rm-upgrade-card { width: 100%; }
  `;

  async onConnected() {
    if (!this.listId) return;
    // If we navigate away while an overlay is open, tear it down.
    this.addCleanup(() => this._closeUpsell());
    const node = await content.getList(this.listId);
    this._render(node);
  }

  /** @param {import("../types").RMListNode | null} node */
  async _render(node) {
    const root = this.shadowRoot;
    if (!root) return;
    if (!node) {
      root.replaceChildren(html`<p>List not found: ${this.listId}</p>`);
      return;
    }
    const children = await Promise.all(node.items.map((c) => this._renderRow(c)));
    root.replaceChildren(html`
      <style>${RMList.styles}</style>
      <h2>${node.title}</h2>
      <ul>${children}</ul>
    `);
  }

  /**
   * Render one child row. Unlocked → a navigating `<a>` (unchanged). Locked →
   * a greyed, focusable `role="button"` that pops the upsell instead of
   * navigating.
   * @param {{id: string, requiredPlays: number}} c
   * @returns {Promise<Node>}
   */
  async _renderRow(c) {
    const item = await content.getItem(c.id);
    const list = item ? null : await content.getList(c.id);
    const target = item ? `/play/${c.id}` : list ? `/list/${c.id}` : null;
    const title = item?.title ?? list?.title ?? c.id;
    const subtitle = item?.titleLine2 ?? list?.titleLine2 ?? "";

    // HTML/Video items are never gated (free browse) — only audio-bearing
    // items can lock, mirroring the player gate. Lists gate via the precomputed
    // descendant walk. Unknown ids (no item, no list) render as plain links.
    const audioBearing =
      item?.type === "Audio" ||
      item?.type === "AudioLyrics" ||
      item?.type === "AudioHTML";
    const unlocked = item
      ? !audioBearing || entitlement.isUnlocked(item.id)
      : list
        ? entitlement.isListUnlocked(list.id)
        : true;

    if (unlocked) {
      return html`<li><a class="row" href="${target ?? '#'}">
        <span class="text">${title}<div class="meta">${subtitle}</div></span>
      </a></li>`;
    }

    const reason = entitlement.status() === "expired" ? "expired" : "locked";
    const lock = document.createElement("span");
    lock.className = "lock";
    lock.setAttribute("aria-hidden", "true");
    lock.innerHTML = LOCK;

    /** @type {EventListener} */
    const onActivate = (e) => {
      e.preventDefault();
      this._showUpsell(reason, /** @type {HTMLElement} */ (e.currentTarget));
    };
    /** @type {EventListener} */
    const onKeydown = (e) => {
      const ev = /** @type {KeyboardEvent} */ (e);
      if (ev.key === "Enter" || ev.key === " " || ev.key === "Spacebar") {
        ev.preventDefault();
        this._showUpsell(reason, /** @type {HTMLElement} */ (ev.currentTarget));
      }
    };

    return html`<li><div
      class="row locked"
      role="button"
      tabindex="0"
      aria-disabled="true"
      aria-label="${title} — locked"
      on:click="${onActivate}"
      on:keydown="${onKeydown}"
    >
      <span class="text">${title}<div class="meta">${subtitle}</div></span>
      ${lock}
    </div></li>`;
  }

  /**
   * Mount the `<rm-upgrade-card>` as an in-place overlay (not a route). Same
   * component the player uses; `embedded` so its dismiss removes the overlay
   * rather than navigating. Idempotent — a second click won't stack overlays.
   * @param {"locked"|"expired"} reason
   * @param {HTMLElement | null} [trigger]  row to restore focus to on close
   */
  _showUpsell(reason, trigger) {
    const root = this.shadowRoot;
    if (!root || root.getElementById("upsell-overlay")) return;

    const overlay = document.createElement("div");
    overlay.id = "upsell-overlay";
    overlay.className = "upsell-overlay";

    const card = /** @type {any} */ (document.createElement("rm-upgrade-card"));
    card.reason = reason;
    card.embedded = true;
    overlay.appendChild(card);

    /** @type {EventListener} */
    const onBackdrop = (e) => { if (e.target === overlay) this._closeUpsell(); };
    /** @type {EventListener} */
    const onDismiss = () => this._closeUpsell();
    overlay.addEventListener("click", onBackdrop);
    card.addEventListener("dismiss", onDismiss);

    this._upsellTrigger = trigger ?? null;
    this._upsellCleanup = () => {
      overlay.removeEventListener("click", onBackdrop);
      card.removeEventListener("dismiss", onDismiss);
      overlay.remove();
    };

    root.appendChild(overlay);
  }

  /** Remove the upsell overlay (if any) and restore focus to the locked row. */
  _closeUpsell() {
    if (!this._upsellCleanup) return;
    this._upsellCleanup();
    this._upsellCleanup = null;
    const trigger = this._upsellTrigger;
    this._upsellTrigger = null;
    trigger?.focus();
  }

  render() { /* imperative render in onConnected */ }
}
